import React, { useState, useEffect } from 'react';
import '../styles/ChatLogs.css';
import { loadGlobalTags } from '../utils/tagutils';

const ChatLogs = () => {
  const [chatLogs, setChatLogs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [date, setDate] = useState('');
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [senderFilter, setSenderFilter] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [activeTab, setActiveTab] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedSessionsForExport, setSelectedSessionsForExport] = useState([]);
  const [sessionTags, setSessionTags] = useState(() => {
    const stored = localStorage.getItem('sessionTags');
    return stored ? JSON.parse(stored) : {};
  });

  const [analytics, setAnalytics] = useState({ totalSessions: 0, totalMessages: 0 });
  const [allTags, setAllTags] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [filterTag, setFilterTag] = useState('');


  useEffect(() => {
    const tags = loadGlobalTags();
    setAllTags(tags);

    const stored = JSON.parse(localStorage.getItem('ChatLogs')) || [];
    setLogs(stored);
    updateAnalytics(stored);

    fetch('/api/analytics/chat')
      .then((res) => res.json())
      .then(setAnalytics)
      .catch(console.error);
  }, []);

  const updateAnalytics = (sessions) => {
    const totalMessages = sessions.reduce((sum, session) => sum + session.messages.length, 0);
    setAnalytics({ totalSessions: sessions.length, totalMessages });
  };

  const filteredLogs = logs.filter(session => {
    const hasKeyword = session.messages.some(m => m.text.toLowerCase().includes(searchTerm.toLowerCase()));
    const hasTag = !filter || (session.tags && session.tags.includes(filter));
    return hasKeyword && hasTag;
  });

  const updateTag = (id, tag) => {
    const updatedTags = { ...sessionTags, [id]: tag };
    setSessionTags(updatedTags);
    localStorage.setItem('sessionTags', JSON.stringify(updatedTags));
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/chat/sessions/${date}`);
      const data = await res.json();
      setSessions(data.sessions || []);
      setError('');
    } catch {
      setError('No sessions found or server error.');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const viewMessages = async (sessionId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/chat/session/${date}/${sessionId}`);

      if (!res.ok) throw new Error('Failed to fetch messages');
      const data = await res.json();
      setSelectedMessages(data.messages || []);
      const tags = new Set();
      (data.messages || []).forEach(msg => {
        const matches = msg.text.match(/#[a-zA-Z0-9_]+/g);
        if (matches) matches.forEach(tag => tags.add(tag));
      });
      setAvailableTags([...tags]);
      setSelectedTag('');
      setSelectedSessionId(sessionId);
      setActiveTab('messages');
    } catch (err) {
      console.error('❌ Error fetching messages:', err);
      setSelectedMessages([]);
    }
  };

  const searchMessages = async () => {
    if (!date || !selectedSession || !searchTerm) return;
    setLoading(true);

    const params = new URLSearchParams({
      date,
      sessionId: selectedSession,
      keyword: searchTerm,
    });

    if (senderFilter) params.append('from', senderFilter);
    if (dateRange.from) params.append('fromTime', new Date(dateRange.from).toISOString());
    if (dateRange.to) params.append('toTime', new Date(dateRange.to).toISOString());

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/chat/search?${params.toString()}`)
      const data = await res.json();
      setSearchResults(data.messages || []);
      setError('');
      setActiveTab('search');
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
      setError('Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const downloadAllSessions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/chat/export/all/${date}`)
      if (!res.ok) throw new Error('Failed to fetch sessions');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${date}-sessions.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Error exporting all sessions:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelectedSessions(prev => prev.includes(id)
      ? prev.filter(sid => sid !== id)
      : [...prev, id]);
  };

  const handleDeleteSelected = () => {
    const filtered = logs.filter(log => !selectedSessions.includes(log.id));
    setLogs(filtered);
    setSelectedSessions([]);
    localStorage.setItem('chatLogs', JSON.stringify(filtered));
    updateAnalytics(filtered);
  };

  const handleTagEdit = (id, newTag) => {
    const updated = logs.map(log => log.id === id ? { ...log, tag: newTag } : log);
    setLogs(updated);
    localStorage.setItem('chatLogs', JSON.stringify(updated));
  };

  const toggleSessionSelection = (sessionId) => {
    setSelectedSessionsForExport(prev =>
      prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const exportSelectedSessions = async () => {
    if (!date || selectedSessionsForExport.length === 0) return;
    try {
      const allData = {};
      for (const sessionId of selectedSessionsForExport) {
        const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/chat/session/${date}/${sessionId}`)
        if (!res.ok) continue;
        const data = await res.json();
        allData[sessionId] = data.messages;
      }

      const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${date}-sessions.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Bulk export failed:', err);
    }
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
  };

  const handleExport = (session, format) => {
    const content = format === 'json'
      ? JSON.stringify(session, null, 2)
      : session.messages.map(m => `[${m.sender}] ${m.text}`).join('\n');

    const blob = new Blob([content], {
      type: format === 'json' ? 'application/json' : 'text/plain'
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `chatlog-${session.id}.${format}`;
    link.click();
  };

  const exportSession = async (sessionId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/chat/session/${date}/${sessionId}`);

      if (!res.ok) throw new Error('Failed to fetch messages');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data.messages, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sessionId}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Error exporting session:', err);
    }
  };

  // ... rest of the component unchanged for now

  return (
    <div className="chatlogs-view">
      <h2>💬 Chat Log Sessions</h2>

      <div className="filters">
        <input
          type="text"
          placeholder="Search messages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All Tags</option>
          {[...new Set(logs.flatMap(log => log.tags || []))].map((tag, idx) => (
            <option key={idx} value={tag}>{tag}</option>
          ))}
        </select>
      </div>

      <div className="chatlogs-container">
        <h2>📚 Chat Logs</h2>
        <div className="analytics">
          <p>Total Sessions: {analytics.totalSessions}</p>
          <p>Total Messages: {analytics.totalMessages}</p>
          {selectedSessions.length > 0 && (
            <button onClick={handleDeleteSelected} className="bulk-delete">
              🗑️ Delete Selected ({selectedSessions.length})
            </button>
          )}
        </div>

        <ul className="chatlog-list">
          {logs.map((log) => (
            <li key={log.id} className="chatlog-item">
              <div className="chatlog-header">
                <input
                  type="checkbox"
                  checked={selectedSessions.includes(log.id)}
                  onChange={() => toggleSelect(log.id)}
                />
                <strong>{log.sessionName}</strong>
                <span className="tag">#{log.tag || 'untagged'}</span>
              </div>
              <div className="chatlog-controls">
                <input
                  type="text"
                  placeholder="Edit tag"
                  defaultValue={log.tag || ''}
                  onBlur={(e) => handleTagEdit(log.id, e.target.value)}
                />
              </div>
              <div className="chatlog-body">
                <p>{log.messages.length} messages</p>
                <ul className="message-list">
                  {log.messages.slice(0, 3).map((msg, i) => (
                    <li key={i} className="message-item">{msg.text}</li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="session-list">
        {filteredLogs.map((session, idx) => (
          <div key={idx} className="chat-session" onClick={() => handleSessionClick(session)}>
            <h4>Session #{session.id}</h4>
            <p>{session.messages[0]?.text?.slice(0, 60)}...</p>
            <div className="chat-tags">
              {(session.tags || []).map((tag, i) => (
                <span key={i} className="chat-tag">#{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selectedSession && (
        <div className="chat-detail">
          <h3>Session #{selectedSession.id}</h3>
          <div className="chat-messages">
            {selectedSession.messages.map((msg, idx) => (
              <div key={idx} className={`chat-msg ${msg.sender.toLowerCase()}`}>
                <strong>{msg.sender}: </strong>
                <span>{msg.text}</span>
              </div>
            ))}
          </div>
          <div className="session-actions">
            <button onClick={() => handleExport(selectedSession, 'txt')}>Export .txt</button>
            <button onClick={() => handleExport(selectedSession, 'json')}>Export .json</button>
            <button onClick={() => setSelectedSession(null)}>Close</button>
          </div>
        </div>
      )}

      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <button onClick={fetchSessions}>Load Sessions</button>
      <button onClick={downloadAllSessions}>Export All Sessions as JSON</button>

      {error && <p className="error">{error}</p>}
      {loading && <p>Loading…</p>}

      {sessions.length > 0 && (
        <div className="session-controls">
          {sessions.map((id) => (
            <div key={id} className="session-tag-row">
              <input
                type="checkbox"
                checked={selectedSessionsForExport.includes(id)}
                onChange={() => toggleSessionSelection(id)}
              />
              <strong>{id}</strong>
              <input
                type="text"
                placeholder="Add tag"
                value={sessionTags[id] || ''}
                onChange={(e) => updateTag(id, e.target.value)}
              />
              <button onClick={() => viewMessages(id)}>View</button>
              <button onClick={() => exportSession(id)}>Export</button>
            </div>
          ))}
          <button onClick={exportSelectedSessions} disabled={selectedSessionsForExport.length === 0}>
            Export Selected Sessions
          </button>

          <select value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)}>
            <option value="">Select Session</option>
            {sessions.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>

          <select onChange={(e) => setFilterTag(e.target.value)}>
            <option value="">All Tags</option>
            {allTags.map((tag, i) => (
              <option key={i} value={tag}>#{tag}</option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button onClick={searchMessages}>Search</button>
        </div>
      )}

      <div className="tab-buttons">
        <button onClick={() => setActiveTab('all')} className={activeTab === 'all' ? 'active' : ''}>All Sessions</button>
        <button onClick={() => setActiveTab('messages')} className={activeTab === 'messages' ? 'active' : ''}>Session Messages</button>
        <button onClick={() => setActiveTab('search')} className={activeTab === 'search' ? 'active' : ''}>Search Results</button>
      </div>

      {availableTags.length > 0 && (
        <div className="tag-filters">
          <strong>Filter by Tag:</strong>
          {availableTags.map(tag => (
            <button
              key={tag}
              className={selectedTag === tag ? 'active' : ''}
              onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'messages' && selectedMessages.length > 0 && (
        <div className="chatlog-details">
          <h3>Messages in: {selectedSessionId}</h3>
          <div className="filters">
            <label>
              Sender:
              <select value={senderFilter} onChange={(e) => setSenderFilter(e.target.value)}>
                <option value="">All</option>
                <option value="user">User</option>
                <option value="bot">Bot</option>
              </select>
            </label>
            <label>
              From:
              <input type="datetime-local" value={dateRange.from} onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })} />
            </label>
            <label>
              To:
              <input type="datetime-local" value={dateRange.to} onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })} />
            </label>
          </div>

          <ul>
            {selectedMessages
              .filter((msg) => {
                if (senderFilter && msg.from !== senderFilter) return false;
                if (dateRange.from && new Date(msg.timestamp) < new Date(dateRange.from)) return false;
                if (dateRange.to && new Date(msg.timestamp) > new Date(dateRange.to)) return false;
                if (selectedTag && !msg.text.includes(selectedTag)) return false;
                return true;
              })
              .map((msg, i) => (
                <li key={i}>
                  <strong>{msg.from}:</strong>{' '}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: msg.text.replace(/(#[a-zA-Z0-9_]+)/g, '<span class="highlight-tag">$1</span>')
                    }}
                  />{' '}
                  <em>({new Date(msg.timestamp).toLocaleString()})</em>
                </li>
              ))}
          </ul>
        </div>
      )}

      {activeTab === 'search' && searchResults.length > 0 && (
        <div className="search-results">
          <h3>Search Results for: "{searchTerm}"</h3>
          <ul>
            {searchResults.map((msg, i) => (
              <li key={i}>
                <strong>{msg.from}:</strong>{' '}
                <span
                  dangerouslySetInnerHTML={{
                    __html: msg.text.replace(/(#[a-zA-Z0-9_]+)/g, '<span class="highlight-tag">$1</span>')
                  }}
                />{' '}
                <em>({new Date(msg.timestamp).toLocaleString()})</em>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}; // closes ChatLogs function

export default ChatLogs;

